import { useEffect, useRef, useState } from 'react';

/**
 * Suaviza a exibição de um indicador de carregamento.
 *
 * Resolve os dois defeitos opostos de mostrar um loader cru:
 *
 * - **Piscada**: carregamento que resolve em 40ms mostra e esconde o loader
 *   antes de o olho registrar, o que aparece como um tremor na tela. Por isso
 *   ele só entra depois de `atrasoMs`.
 * - **Corte**: uma vez visível, sumir no quadro seguinte deixa a animação pela
 *   metade — no caso do líquido, uma onda congelada no meio da subida. Por
 *   isso ele fica pelo menos `minimoMs` depois de aparecer.
 *
 * O resultado é: carregamento rápido não mostra nada, e carregamento lento
 * mostra uma animação inteira.
 */
export function useCarregamentoSuave(
  carregando: boolean,
  { atrasoMs = 180, minimoMs = 900 }: { atrasoMs?: number; minimoMs?: number } = {},
): boolean {
  const [visivel, setVisivel] = useState(false);
  const mostradoEm = useRef<number | null>(null);

  useEffect(() => {
    if (carregando) {
      // Só aparece se a espera passar do limiar — abaixo dele, ninguém precisa
      // saber que houve espera.
      const timer = setTimeout(() => {
        mostradoEm.current = Date.now();
        setVisivel(true);
      }, atrasoMs);
      return () => {
        clearTimeout(timer);
      };
    }

    if (!visivel) return;

    const decorrido = Date.now() - (mostradoEm.current ?? Date.now());
    const restante = Math.max(0, minimoMs - decorrido);

    const timer = setTimeout(() => {
      mostradoEm.current = null;
      setVisivel(false);
    }, restante);
    return () => {
      clearTimeout(timer);
    };
  }, [carregando, visivel, atrasoMs, minimoMs]);

  return visivel;
}
